import {
  type ExpandedState,
  type OnChangeFn,
  type Row,
  type TableOptions,
} from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';

import type {
  DataTableDetailExpandableConfig,
  DataTableExpandableConfig,
  DataTableExpandableMode,
  DataTableExpandableVariant,
} from './types';

const resolveStateUpdate = <TState>(
  updater: TState | ((previousState: TState) => TState),
  previousState: TState,
) => {
  if (typeof updater === 'function') {
    return (updater as (state: TState) => TState)(previousState);
  }

  return updater;
};

const expandedRowIdsToState = (expandedRowIds: string[]): ExpandedState => {
  return expandedRowIds.reduce<Record<string, boolean>>((state, expandedRowId) => {
    state[expandedRowId] = true;
    return state;
  }, {});
};

const normalizeExpandedRowIdsForMultiple = (expandedRowIds: string[]): string[] => {
  return Array.from(new Set(expandedRowIds)).sort();
};

// TanStack represents "all rows expanded" as the literal `true`. We do not support global
// "expand all" behavior in DataTable, so when this appears we keep the previously-expanded
// ids instead of collapsing everything.
const expandedStateToRowIds = (
  expandedState: ExpandedState,
  previousExpandedRowIds: string[],
): string[] => {
  if (expandedState === true) {
    return previousExpandedRowIds;
  }

  return Object.entries(expandedState)
    .filter(([, expanded]) => expanded)
    .map(([rowId]) => rowId);
};

const normalizeExpandedRowIds = (
  expandedRowIds: string[] | undefined,
  mode: DataTableExpandableMode,
) => {
  if (expandedRowIds == null || expandedRowIds.length === 0) {
    return [];
  }

  const uniqueExpandedRowIds = Array.from(new Set(expandedRowIds));

  if (mode === 'accordion') {
    return uniqueExpandedRowIds.slice(-1);
  }

  return normalizeExpandedRowIdsForMultiple(uniqueExpandedRowIds);
};

const areRowIdArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((rowId, index) => rowId === right[index]);
};

export const rowHasAncestorInSet = <TData extends object>(
  row: Row<TData>,
  ancestorIds: Set<string>,
  rowsById: Record<string, Row<TData>>,
) => {
  let parentId = row.parentId;

  while (parentId != null) {
    if (ancestorIds.has(parentId)) {
      return true;
    }

    parentId = rowsById[parentId]?.parentId;
  }

  return false;
};

const resolveExpandableVariant = <TData extends object>(
  expandable: DataTableExpandableConfig<TData> | undefined,
): DataTableExpandableVariant | null => {
  if (expandable == null) {
    return null;
  }

  return expandable.variant;
};

type UseDataTableRowExpansionResult<TData extends object> = {
  expandableEnabled: boolean;
  detailExpandableMode: boolean;
  subRowsExpandableMode: boolean;
  detailExpandable: DataTableDetailExpandableConfig<TData> | null;
  expansionAnimationDurationMs: number;
  resolvedExpandedRowIdSet: Set<string>;
  collapsingExpandedRowIdSet: Set<string>;
  expandedStateForTable: ExpandedState;
  onExpandedChange: OnChangeFn<ExpandedState>;
  resolvedGetSubRows: TableOptions<TData>['getSubRows'] | undefined;
  resolvedGetRowCanExpand: ((row: Row<TData>) => boolean) | undefined;
};

const useDataTableRowExpansion = <TData extends object>(
  expandable: DataTableExpandableConfig<TData> | undefined,
): UseDataTableRowExpansionResult<TData> => {
  const expandableMode = expandable?.mode ?? 'multiple';
  const expandableVariant = resolveExpandableVariant(expandable);
  const expandableEnabled = expandableVariant != null;
  const detailExpandableMode = expandableVariant === 'detail';
  const subRowsExpandableMode = expandableVariant === 'subRows';
  const detailExpandable =
    detailExpandableMode && expandable?.variant === 'detail' ? expandable : null;
  const expansionAnimationDurationMs = Math.max(0, expandable?.durationMs ?? 200);

  const defaultExpandedRowIds = useMemo(
    () => normalizeExpandedRowIds(expandable?.defaultExpandedRowIds, expandableMode),
    [expandable?.defaultExpandedRowIds, expandableMode],
  );
  const [uncontrolledExpandedRowIds, setUncontrolledExpandedRowIds] =
    useState<string[]>(defaultExpandedRowIds);

  const expandedRowIdsSource = expandable?.expandedRowIds ?? uncontrolledExpandedRowIds;
  const resolvedExpandedRowIds = useMemo(
    () => normalizeExpandedRowIds(expandedRowIdsSource, expandableMode),
    [expandableMode, expandedRowIdsSource],
  );
  const logicalExpandedState = useMemo(
    () => expandedRowIdsToState(resolvedExpandedRowIds),
    [resolvedExpandedRowIds],
  );

  // For sub-rows, TanStack removes collapsed rows from `getRowModel()` immediately, which
  // kills the exit transition. We keep collapsing rows here until their animation finishes,
  // then prune them. Detail mode doesn't need this because DataTableExpandedRow owns its own
  // mount/unmount lifecycle.
  const [animatedExpandedRowIds, setAnimatedExpandedRowIds] =
    useState<string[]>(resolvedExpandedRowIds);
  const collapseTimeoutsRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  useEffect(() => {
    const collapseTimeouts = collapseTimeoutsRef.current;
    return () => {
      collapseTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
      collapseTimeouts.clear();
    };
  }, []);

  useEffect(() => {
    if (!subRowsExpandableMode) {
      collapseTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
      collapseTimeoutsRef.current.clear();
      setAnimatedExpandedRowIds((previous) =>
        areRowIdArraysEqual(previous, resolvedExpandedRowIds) ? previous : resolvedExpandedRowIds,
      );
      return;
    }

    const resolvedSet = new Set(resolvedExpandedRowIds);

    resolvedSet.forEach((rowId) => {
      const timeoutId = collapseTimeoutsRef.current.get(rowId);
      if (timeoutId != null) {
        clearTimeout(timeoutId);
        collapseTimeoutsRef.current.delete(rowId);
      }
    });

    setAnimatedExpandedRowIds((previous) => {
      const previousSet = new Set(previous);
      const addedIds = resolvedExpandedRowIds.filter((rowId) => !previousSet.has(rowId));
      if (addedIds.length === 0) {
        return previous;
      }
      return [...previous, ...addedIds];
    });

    animatedExpandedRowIds.forEach((rowId) => {
      if (resolvedSet.has(rowId)) {
        return;
      }
      if (collapseTimeoutsRef.current.has(rowId)) {
        return;
      }
      collapseTimeoutsRef.current.set(
        rowId,
        setTimeout(() => {
          setAnimatedExpandedRowIds((previous) =>
            previous.filter((expandedRowId) => expandedRowId !== rowId),
          );
          collapseTimeoutsRef.current.delete(rowId);
        }, expansionAnimationDurationMs),
      );
    });
  }, [
    animatedExpandedRowIds,
    expansionAnimationDurationMs,
    resolvedExpandedRowIds,
    subRowsExpandableMode,
  ]);

  const expandedRowIdsForState = subRowsExpandableMode
    ? animatedExpandedRowIds
    : resolvedExpandedRowIds;
  const expandedStateForTable = useMemo(
    () => expandedRowIdsToState(expandedRowIdsForState),
    [expandedRowIdsForState],
  );
  const resolvedExpandedRowIdSet = useMemo(
    () => new Set(resolvedExpandedRowIds),
    [resolvedExpandedRowIds],
  );
  const collapsingExpandedRowIdSet = useMemo(
    () => new Set(animatedExpandedRowIds.filter((rowId) => !resolvedExpandedRowIdSet.has(rowId))),
    [animatedExpandedRowIds, resolvedExpandedRowIdSet],
  );

  const onExpandedChange: OnChangeFn<ExpandedState> = (updater) => {
    if (!expandableEnabled) {
      return;
    }

    const nextExpandedState = resolveStateUpdate(updater, logicalExpandedState);
    const nextExpandedRowIds = expandedStateToRowIds(nextExpandedState, resolvedExpandedRowIds);

    let normalizedExpandedRowIds: string[];
    if (expandableMode === 'accordion') {
      // Prefer a newly-added row over the last one in the updated state. TanStack's
      // toggleExpanded produces `{ ...prev, [rowId]: true }`, so the newly added row is
      // usually at the end, but we don't want to rely on object-key order.
      const addedRowId = nextExpandedRowIds.find(
        (rowId) => !resolvedExpandedRowIds.includes(rowId),
      );
      normalizedExpandedRowIds = addedRowId != null ? [addedRowId] : nextExpandedRowIds.slice(-1);
    } else {
      normalizedExpandedRowIds = normalizeExpandedRowIds(nextExpandedRowIds, expandableMode);
    }

    if (areRowIdArraysEqual(normalizedExpandedRowIds, resolvedExpandedRowIds)) {
      return;
    }

    if (expandable?.expandedRowIds == null) {
      setUncontrolledExpandedRowIds(normalizedExpandedRowIds);
    }

    expandable?.onExpandedRowIdsChange?.(normalizedExpandedRowIds);
  };

  const resolvedGetSubRows =
    subRowsExpandableMode && expandable?.variant === 'subRows' ? expandable.getSubRows : undefined;
  const resolvedGetRowCanExpand =
    expandable?.getRowCanExpand ?? (detailExpandableMode ? () => true : undefined);

  return {
    expandableEnabled,
    detailExpandableMode,
    subRowsExpandableMode,
    detailExpandable,
    expansionAnimationDurationMs,
    resolvedExpandedRowIdSet,
    collapsingExpandedRowIdSet,
    expandedStateForTable,
    onExpandedChange,
    resolvedGetSubRows,
    resolvedGetRowCanExpand,
  };
};

export default useDataTableRowExpansion;
